import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { benachrichtigeRechteAenderung } from '../../../hooks/useRechte';
import { gueltigBisIso } from '../../../lib/rechteMatrix';
import { Benutzer } from '../../../types';
import { API_URL, STIL, fehlerText } from './rechteUtil';

interface ManuelleZuweisung {
  zuweisung_id: number;
  benutzer_id: number;
  auftrag_id: number | null;
  projekt_id: number | null;
  gueltig_bis: string | null;
  erstellt_von: number | null;
  erstellt_am: string;
  quelle: 'manuell';
}

interface AbgeleiteteZuweisung {
  benutzer_id: number;
  auftrag_id: number | null;
  projekt_id: number | null;
  quelle: 'einsatz';
}

interface Zuweisungen {
  manuell: ManuelleZuweisung[];
  abgeleitet: AbgeleiteteZuweisung[];
}

interface AuftragKurz { auftrag_id: number; auftrag_nr: string }
interface ProjektKurz { projekt_id: number; projekt_nr: string; bezeichnung: string }

/** Tab "Zuweisungen": manuelle Zuweisungen pflegen, aus Einsätzen abgeleitete nur anzeigen. */
const RechteZuweisungen: React.FC = () => {
  const [zuweisungen, setZuweisungen] = useState<Zuweisungen>({ manuell: [], abgeleitet: [] });
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [auftraege, setAuftraege] = useState<AuftragKurz[]>([]);
  const [projekte, setProjekte] = useState<ProjektKurz[]>([]);
  // Auftrag-/Projekt-Modul kann im Mandanten fehlen: dann leere Liste + Hinweis statt Fehler
  const [ohneModul, setOhneModul] = useState({ auftrag: false, projekt: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [form, setForm] = useState({ benutzer_id: '', art: 'auftrag' as 'auftrag' | 'projekt', ziel: '', gueltig_bis: '' });

  const laden = useCallback(() => {
    // allSettled: ein fehlendes Auftrag-/Projekt-Modul (403/404) darf den Tab nicht
    // sprengen - nur Zuweisungen und Benutzer sind Pflicht.
    return Promise.allSettled([
      axios.get<Zuweisungen>(`${API_URL}/api/rechte/zuweisungen`),
      axios.get<Benutzer[]>(`${API_URL}/api/benutzer`),
      axios.get<AuftragKurz[]>(`${API_URL}/api/auftraege`),
      axios.get<ProjektKurz[]>(`${API_URL}/api/projekte`),
    ])
      .then(([z, b, a, p]) => {
        if (z.status === 'rejected' || b.status === 'rejected') {
          const grund = z.status === 'rejected' ? z.reason : (b as PromiseRejectedResult).reason;
          setFehler(fehlerText(grund, 'Zuweisungen konnten nicht geladen werden.'));
          return;
        }
        setZuweisungen(z.value.data);
        setBenutzer(b.value.data);
        setAuftraege(a.status === 'fulfilled' ? a.value.data : []);
        setProjekte(p.status === 'fulfilled' ? p.value.data : []);
        setOhneModul({ auftrag: a.status === 'rejected', projekt: p.status === 'rejected' });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const benutzerName = (id: number) => benutzer.find(b => b.benutzer_id === id)?.name ?? `Benutzer ${id}`;
  const zielText = (z: { auftrag_id: number | null; projekt_id: number | null }) => {
    if (z.auftrag_id != null) {
      const a = auftraege.find(x => x.auftrag_id === z.auftrag_id);
      return `Auftrag ${a ? a.auftrag_nr : `#${z.auftrag_id}`}`;
    }
    if (z.projekt_id != null) {
      const p = projekte.find(x => x.projekt_id === z.projekt_id);
      return `Projekt ${p ? `${p.projekt_nr} ${p.bezeichnung}` : `#${z.projekt_id}`}`;
    }
    return '—';
  };

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.benutzer_id || !form.ziel) {
      setFehler('Bitte Benutzer und Auftrag bzw. Projekt wählen.');
      return;
    }
    setSaving(true);
    setFehler(null);
    try {
      const body: Record<string, unknown> = { benutzer_id: Number(form.benutzer_id) };
      body[form.art === 'auftrag' ? 'auftrag_id' : 'projekt_id'] = Number(form.ziel);
      const bis = gueltigBisIso(form.gueltig_bis);
      if (bis) body.gueltig_bis = bis;
      await axios.post(`${API_URL}/api/rechte/zuweisungen`, body);
      benachrichtigeRechteAenderung();
      setForm(f => ({ ...f, ziel: '', gueltig_bis: '' }));
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Zuweisung konnte nicht angelegt werden.'));
    } finally {
      setSaving(false);
    }
  };

  const loeschen = async (z: ManuelleZuweisung) => {
    if (!window.confirm(`Zuweisung von ${benutzerName(z.benutzer_id)} zu ${zielText(z)} entfernen?`)) return;
    setSaving(true);
    setFehler(null);
    try {
      await axios.delete(`${API_URL}/api/rechte/zuweisungen/${z.zuweisung_id}`);
      benachrichtigeRechteAenderung();
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Zuweisung konnte nicht entfernt werden.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Lade Zuweisungen…</div>;

  const ziele = form.art === 'auftrag'
    ? auftraege.map(a => ({ id: a.auftrag_id, text: a.auftrag_nr }))
    : projekte.map(p => ({ id: p.projekt_id, text: `${p.projekt_nr} ${p.bezeichnung}` }));

  return (
    <div>
      {fehler && <div style={STIL.fehler}>{fehler}</div>}
      <div style={STIL.hinweis}>
        Zuweisungen geben Rollen mit dem Bereich „Zugewiesene“ Zugriff auf einzelne Aufträge oder Projekte.
      </div>

      <form onSubmit={anlegen} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={STIL.label}>
          Benutzer
          <select value={form.benutzer_id} onChange={e => setForm(f => ({ ...f, benutzer_id: e.target.value }))} style={{ ...STIL.select, minWidth: 200 }} required>
            <option value="">— wählen —</option>
            {benutzer.map(b => <option key={b.benutzer_id} value={b.benutzer_id}>{b.name}</option>)}
          </select>
        </label>
        <div style={STIL.label}>
          Zuweisen für
          <span style={{ display: 'flex', gap: 12, alignItems: 'center', minHeight: 30 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="radio" name="zuweisung-art" checked={form.art === 'auftrag'} onChange={() => setForm(f => ({ ...f, art: 'auftrag', ziel: '' }))} />
              Auftrag
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="radio" name="zuweisung-art" checked={form.art === 'projekt'} onChange={() => setForm(f => ({ ...f, art: 'projekt', ziel: '' }))} />
              Projekt
            </label>
          </span>
        </div>
        <label style={STIL.label}>
          {form.art === 'auftrag' ? 'Auftrag' : 'Projekt'}
          <select value={form.ziel} onChange={e => setForm(f => ({ ...f, ziel: e.target.value }))} style={{ ...STIL.select, minWidth: 220 }} required>
            <option value="">— wählen —</option>
            {ziele.map(z => <option key={z.id} value={z.id}>{z.text}</option>)}
          </select>
          {ohneModul[form.art] && (
            <span style={STIL.hinweis}>Modul nicht aktiv</span>
          )}
        </label>
        <label style={STIL.label}>
          Gültig bis (optional)
          <input type="date" value={form.gueltig_bis} onChange={e => setForm(f => ({ ...f, gueltig_bis: e.target.value }))} style={{ padding: '4px 8px', borderRadius: 0 }} />
        </label>
        <button type="submit" className="k-btn k-btn--primary" disabled={saving}>+ Zuweisen</button>
      </form>

      <div style={STIL.untertitel}>Manuelle Zuweisungen</div>
      {zuweisungen.manuell.length === 0 ? (
        <div style={STIL.hinweis}>Keine manuellen Zuweisungen.</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={STIL.th}>Benutzer</th>
              <th style={STIL.th}>Ziel</th>
              <th style={STIL.th}>Gültig bis</th>
              <th style={STIL.th} />
            </tr>
          </thead>
          <tbody>
            {zuweisungen.manuell.map(z => (
              <tr key={z.zuweisung_id}>
                <td style={STIL.td}>{benutzerName(z.benutzer_id)}</td>
                <td style={STIL.td}>{zielText(z)}</td>
                <td style={STIL.td}>{z.gueltig_bis ? new Date(z.gueltig_bis).toLocaleDateString('de-DE') : 'unbefristet'}</td>
                <td style={{ ...STIL.td, textAlign: 'right' }}>
                  <button type="button" className="k-btn k-btn--sm k-btn--danger" disabled={saving} onClick={() => loeschen(z)}>
                    Entfernen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={STIL.untertitel}>Aus Einsatzplanung abgeleitet</div>
      {zuweisungen.abgeleitet.length === 0 ? (
        <div style={STIL.hinweis}>Keine abgeleiteten Zuweisungen.</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {zuweisungen.abgeleitet.map((z, i) => (
              <tr key={`${z.benutzer_id}-${z.auftrag_id ?? ''}-${z.projekt_id ?? ''}-${i}`}>
                <td style={STIL.td}>{benutzerName(z.benutzer_id)}</td>
                <td style={STIL.td}>{zielText(z)}</td>
                <td style={STIL.td}><span style={STIL.badge}>aus Einsatz</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RechteZuweisungen;
